import { Component, DestroyRef, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { FormBuilderEditCultureService } from './form-builder-edit-culture.service';
import { FormBuilderLocalizedInputComponent } from './form-builder-localized-input.component';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { DynamicApiService, DynamicForm } from '../dynamic-api.service';
import {
  LocalizedText,
  FORM_AI_OUTPUT_FORMATS,
  FORM_AI_RECOMMENDATION_STRATEGIES,
  FormAiOutcome,
  ensureAiOutcomePromptDefaults,
  readLocalizedText,
  writeLocalizedText
} from '../../../shared/dynamic-form/form-schema.models';

@Component({
  standalone: true,
  selector: 'ain-form-builder-ai-outcome-panel',
  imports: [
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    TextareaModule,
    AinFormFieldComponent,
    InfoHintComponent,
    FormBuilderLocalizedInputComponent
  ],
  template: `
    <section class="ai-panel">
      <ain-info-hint
        [titleKey]="'dynamic.builder.ai.infoTitle'"
        [descriptionKey]="'dynamic.builder.ai.infoDescription'"
        [bulletKeys]="[
          'dynamic.builder.ai.infoBulletPlaceholders',
          'dynamic.builder.ai.infoBulletRuntime'
        ]" />

      <div class="checkbox-row">
        <p-checkbox
          binary
          [ngModel]="aiOutcome().enabled"
          [disabled]="!canEdit()"
          (ngModelChange)="patch({ enabled: $event })" />
        <label>{{ t('dynamic.builder.ai.enabled') }}</label>
      </div>

      <ain-form-field [label]="t('dynamic.builder.ai.useCaseKey')">
        <input
          pInputText
          [ngModel]="aiOutcome().useCaseKey ?? ''"
          [disabled]="!canEdit()"
          (ngModelChange)="patch({ useCaseKey: $event || undefined })" />
      </ain-form-field>

      <ain-form-builder-localized-input
        [label]="t('dynamic.builder.ai.systemPrompt')"
        [text]="aiOutcome().systemPrompt"
        [multiline]="true"
        [rows]="3"
        [disabled]="!canEdit()"
        (textChange)="updateSystemPromptText($event)" />

      <ain-form-field [label]="t('dynamic.builder.ai.userPromptTemplate')">
        <textarea
          pTextarea
          rows="4"
          [ngModel]="aiOutcome().userPromptTemplate ?? ''"
          [disabled]="!canEdit()"
          (ngModelChange)="patch({ userPromptTemplate: $event || undefined })"></textarea>
        <small class="hint">{{ t('dynamic.builder.ai.placeholderHint') }}</small>
      </ain-form-field>

      <h4>{{ t('dynamic.builder.ai.rag') }}</h4>
      <div class="checkbox-row">
        <p-checkbox
          binary
          [ngModel]="aiOutcome().rag?.enabled"
          [disabled]="!canEdit()"
          (ngModelChange)="patchRag({ enabled: $event })" />
        <label>{{ t('dynamic.builder.ai.ragEnabled') }}</label>
      </div>
      <ain-form-field [label]="t('dynamic.builder.ai.knowledgeScope')">
        <p-multiSelect
          [options]="knowledgeScopeOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="aiOutcome().rag?.knowledgeScope ?? []"
          [disabled]="!canEdit()"
          [filter]="true"
          appendTo="body"
          (ngModelChange)="patchRag({ knowledgeScope: $event })" />
      </ain-form-field>
      <ain-form-field [label]="t('dynamic.builder.ai.addScope')">
        <div class="scope-row">
          <input
            pInputText
            [(ngModel)]="newScopeTag"
            [disabled]="!canEdit()"
            [placeholder]="t('dynamic.builder.ai.scopePlaceholder')" />
          @if (canEdit()) {
            <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.ai.addScopeButton')" (click)="addKnowledgeScope()"></button>
          }
        </div>
      </ain-form-field>

      <h4>{{ t('dynamic.builder.ai.recommendations') }}</h4>
      <ain-form-field [label]="t('dynamic.builder.ai.formsStrategy')">
        <p-select
          [options]="strategyOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="aiOutcome().recommendations?.forms?.strategy ?? ''"
          [disabled]="!canEdit()"
          appendTo="body"
          (ngModelChange)="patchFormRecommendations({ strategy: $event || undefined })" />
      </ain-form-field>
      <ain-form-field [label]="t('dynamic.builder.ai.formKeys')">
        <p-multiSelect
          [options]="formOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="aiOutcome().recommendations?.forms?.formKeys ?? []"
          [disabled]="!canEdit()"
          [filter]="true"
          appendTo="body"
          (ngModelChange)="patchFormRecommendations({ formKeys: $event })" />
      </ain-form-field>
      <ain-form-field [label]="t('dynamic.builder.ai.productsStrategy')">
        <p-select
          [options]="strategyOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="aiOutcome().recommendations?.products?.strategy ?? ''"
          [disabled]="!canEdit()"
          appendTo="body"
          (ngModelChange)="patchProductRecommendations({ strategy: $event || undefined })" />
      </ain-form-field>

      <div class="params-block">
        <div class="params-header">
          <strong>{{ t('dynamic.builder.ai.productParams') }}</strong>
          @if (canEdit()) {
            <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.ai.addParam')" (click)="addProductParam()"></button>
          }
        </div>
        @for (row of productParamRows(); track $index; let idx = $index) {
          <div class="param-row">
            <input
              pInputText
              [placeholder]="t('dynamic.builder.ai.paramKey')"
              [ngModel]="row.key"
              [disabled]="!canEdit()"
              (ngModelChange)="updateProductParam(idx, $event, row.value)" />
            <input
              pInputText
              [placeholder]="t('dynamic.builder.ai.paramValue')"
              [ngModel]="row.value"
              [disabled]="!canEdit()"
              (ngModelChange)="updateProductParam(idx, row.key, $event)" />
            @if (canEdit()) {
              <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeProductParam(idx)"></button>
            }
          </div>
        }
      </div>

      <h4>{{ t('dynamic.builder.ai.output') }}</h4>
      <ain-form-field [label]="t('dynamic.builder.ai.outputFormat')">
        <p-select
          [options]="outputFormatOptions()"
          optionLabel="label"
          optionValue="value"
          [ngModel]="aiOutcome().output?.format ?? ''"
          [disabled]="!canEdit()"
          appendTo="body"
          (ngModelChange)="patchOutput({ format: $event || undefined })" />
      </ain-form-field>
      <div class="checkbox-row">
        <p-checkbox
          binary
          [ngModel]="aiOutcome().output?.showScore"
          [disabled]="!canEdit()"
          (ngModelChange)="patchOutput({ showScore: $event })" />
        <label>{{ t('dynamic.builder.ai.showScore') }}</label>
      </div>
      <div class="checkbox-row">
        <p-checkbox
          binary
          [ngModel]="aiOutcome().output?.showRecommendations"
          [disabled]="!canEdit()"
          (ngModelChange)="patchOutput({ showRecommendations: $event })" />
        <label>{{ t('dynamic.builder.ai.showRecommendations') }}</label>
      </div>

      <h4>{{ t('dynamic.builder.ai.guardrail') }}</h4>
      <div class="checkbox-row">
        <p-checkbox
          binary
          [ngModel]="aiOutcome().guardrail?.advisoryOnly ?? true"
          [disabled]="!canEdit()"
          (ngModelChange)="patchGuardrail({ advisoryOnly: $event })" />
        <label>{{ t('dynamic.builder.ai.advisoryOnly') }}</label>
      </div>
      <ain-form-builder-localized-input
        [label]="t('dynamic.builder.ai.disclaimer')"
        [text]="aiOutcome().guardrail?.disclaimer"
        [multiline]="true"
        [rows]="2"
        [disabled]="!canEdit()"
        (textChange)="updateDisclaimerText($event)" />
    </section>
  `,
  styles: [`
    .ai-panel { display: grid; gap: 12px; padding: 4px 0; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .hint { color: var(--text-color-secondary); }
    h4 { margin: 8px 0 0; }
    .params-block { display: grid; gap: 8px; }
    .params-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .param-row { display: grid; grid-template-columns: 1fr 1fr auto; gap: 8px; align-items: center; }
    .scope-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  `]
})
export class FormBuilderAiOutcomePanelComponent {
  private readonly localization = inject(LocalizationService);
  private readonly editCultureService = inject(FormBuilderEditCultureService);
  private readonly dynamicApi = inject(DynamicApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly aiOutcome = input<FormAiOutcome>({ enabled: false });
  readonly culture = input.required<string>();
  readonly canEdit = input(true);

  readonly aiOutcomeChange = output<FormAiOutcome>();

  readonly forms = signal<DynamicForm[]>([]);
  newScopeTag = '';

  readonly strategyOptions = computed(() =>
    FORM_AI_RECOMMENDATION_STRATEGIES.map(value => ({
      label: this.t(`dynamic.builder.ai.strategy.${value}`),
      value
    }))
  );

  readonly outputFormatOptions = computed(() =>
    FORM_AI_OUTPUT_FORMATS.map(value => ({
      label: this.t(`dynamic.builder.ai.format.${value}`),
      value
    }))
  );

  readonly formOptions = computed(() =>
    this.forms().map(form => ({
      label: `${form.name} (${form.id})`,
      value: form.id
    }))
  );

  readonly knowledgeScopeOptions = computed(() => {
    const scopes = new Set(this.aiOutcome().rag?.knowledgeScope ?? []);
    return [...scopes].map(value => ({ label: value, value }));
  });

  readonly productParamRows = computed(() => {
    const params = this.aiOutcome().recommendations?.products?.params ?? {};
    const rows = Object.entries(params).map(([key, value]) => ({
      key,
      value: value === null || value === undefined ? '' : String(value)
    }));
    return rows.length ? rows : [{ key: '', value: '' }];
  });

  constructor() {
    this.dynamicApi.listForms()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: items => this.forms.set(items),
        error: () => this.forms.set([])
      });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  systemPromptValue(): string {
    return readLocalizedText(this.aiOutcome().systemPrompt, this.culture());
  }

  disclaimerValue(): string {
    return readLocalizedText(this.aiOutcome().guardrail?.disclaimer, this.culture());
  }

  patch(patch: Partial<FormAiOutcome>): void {
    const merged = { ...(this.aiOutcome() ?? { enabled: false }), ...patch };
    if (merged.enabled) {
      this.aiOutcomeChange.emit(ensureAiOutcomePromptDefaults(merged, this.editCultureService.editCulture()));
      return;
    }

    this.aiOutcomeChange.emit(merged);
  }

  updateSystemPromptText(systemPrompt: FormAiOutcome['systemPrompt']): void {
    this.patch({ systemPrompt });
  }

  updateSystemPrompt(value: string): void {
    this.patch({
      systemPrompt: writeLocalizedText(this.aiOutcome().systemPrompt, this.editCultureService.editCulture(), value)
    });
  }

  patchRag(patch: Partial<NonNullable<FormAiOutcome['rag']>>): void {
    const current = this.aiOutcome() ?? { enabled: false };
    this.aiOutcomeChange.emit({
      ...current,
      rag: { ...(current.rag ?? {}), ...patch }
    });
  }

  patchFormRecommendations(patch: Partial<NonNullable<NonNullable<FormAiOutcome['recommendations']>['forms']>>): void {
    const current = this.aiOutcome() ?? { enabled: false };
    this.aiOutcomeChange.emit({
      ...current,
      recommendations: {
        ...(current.recommendations ?? {}),
        forms: { ...(current.recommendations?.forms ?? {}), ...patch }
      }
    });
  }

  patchProductRecommendations(patch: Partial<NonNullable<NonNullable<FormAiOutcome['recommendations']>['products']>>): void {
    const current = this.aiOutcome() ?? { enabled: false };
    this.aiOutcomeChange.emit({
      ...current,
      recommendations: {
        ...(current.recommendations ?? {}),
        products: { ...(current.recommendations?.products ?? {}), ...patch }
      }
    });
  }

  patchOutput(patch: Partial<NonNullable<FormAiOutcome['output']>>): void {
    const current = this.aiOutcome() ?? { enabled: false };
    this.aiOutcomeChange.emit({
      ...current,
      output: { ...(current.output ?? {}), ...patch }
    });
  }

  patchGuardrail(patch: Partial<NonNullable<FormAiOutcome['guardrail']>>): void {
    const current = this.aiOutcome() ?? { enabled: false };
    this.aiOutcomeChange.emit({
      ...current,
      guardrail: { ...(current.guardrail ?? { advisoryOnly: true }), ...patch }
    });
  }

  updateDisclaimerText(disclaimer: LocalizedText | undefined): void {
    this.patchGuardrail({ disclaimer });
  }

  updateDisclaimer(value: string): void {
    this.patchGuardrail({
      disclaimer: writeLocalizedText(this.aiOutcome().guardrail?.disclaimer, this.editCultureService.editCulture(), value)
    });
  }

  addProductParam(): void {
    this.patchProductRecommendations({
      params: { ...(this.aiOutcome().recommendations?.products?.params ?? {}), '': '' }
    });
  }

  updateProductParam(index: number, key: string, value: string): void {
    const rows = [...this.productParamRows()];
    rows[index] = { key, value };
    const params: Record<string, string> = {};
    for (const row of rows) {
      if (row.key.trim()) {
        params[row.key.trim()] = row.value;
      }
    }
    this.patchProductRecommendations({ params });
  }

  removeProductParam(index: number): void {
    const rows = this.productParamRows().filter((_, idx) => idx !== index);
    const params: Record<string, string> = {};
    for (const row of rows) {
      if (row.key.trim()) {
        params[row.key.trim()] = row.value;
      }
    }
    this.patchProductRecommendations({ params: Object.keys(params).length ? params : {} });
  }

  addKnowledgeScope(): void {
    const tag = this.newScopeTag.trim();
    if (!tag) {
      return;
    }
    const current = this.aiOutcome().rag?.knowledgeScope ?? [];
    if (current.includes(tag)) {
      this.newScopeTag = '';
      return;
    }
    this.patchRag({ knowledgeScope: [...current, tag] });
    this.newScopeTag = '';
  }
}
